import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import * as Sentry from '@sentry/react'
import { queryClient } from './lib/queryClient'
import './index.css'
import './i18n'
import App from './App.tsx'
import { setupPwaAutoUpdate } from './lib/pwaAutoUpdate'
import { installModalDrag } from './lib/modalDrag'
import { CHUNK_RECOVERY_EXHAUSTED_MARKER, isChunkLoadError } from './lib/chunkErrorRecovery'

setupPwaAutoUpdate()
installModalDrag()

// Sentry initialization — no-op when VITE_SENTRY_DSN is not set.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    // Sentry noise filter — drops error classes that are either third-party
    // noise or auto-healed by the app, so real defects stay visible.
    // Each rule matches ONE exact class; every other error reaches Sentry.
    beforeSend(event) {
      const values = event.exception?.values ?? [];
      for (const ex of values) {
        const value = typeof ex.value === 'string' ? ex.value : '';

        // (1) ORPHANED Firebase Messaging rejection (ADAPTHUB-B).
        // On iOS Safari the SDK's messaging component factory runs an async
        // isWindowSupported() check that rejects with "messaging/unsupported-
        // browser". The rejection has no awaiter (our sync guard in firebase.ts
        // catches only the synchronous getMessaging() throw), so it surfaces as
        // onunhandledrejection with only SDK frames in the stack. Harmless: push
        // simply stays disabled on those devices.
        if (value.includes('messaging/unsupported-browser')) {
          return null;
        }

        // (2) Cloudflare Turnstile "Nothing to reset found for provided
        // container" (ADAPTHUB-C). The throw happens inside Turnstile's OWN
        // setTimeout (mechanism: auto.browser.browserapierrors.setTimeout)
        // when its internal crash-retry runs after our cleanup already
        // removed the widget container — so no app-side try/catch can reach
        // it. The App Check flow itself handles the failure (it rejects and
        // the app continues without a token).
        if (value.includes('Nothing to reset found for provided container')) {
          return null;
        }

        // (3) Stale-deploy chunk errors (ADAPTHUB-45 / 4Q / 92 / 93 / 94 /
        // 95). A long-open window requests a hashed chunk that the new
        // deployment already deleted; the recovery ladder (reload → service
        // worker + cache purge) heals the client automatically, so the raw
        // error is noise. The ONE exception is the exhausted-ladder marker:
        // it means the automatic healing failed and the user needs help —
        // that stays in Sentry.
        if (value.includes(CHUNK_RECOVERY_EXHAUSTED_MARKER)) {
          continue;
        }
        if (isChunkLoadError(value)) {
          return null;
        }
      }
      return event;
    },
  });
}

const isDev = import.meta.env.DEV

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element #root not found in document');

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
      {isDev && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>,
)
